/**
 * Client for the response service's real-time streaming endpoint
 * (`POST /llm/avatar-speak-stream`, produces `text/event-stream`).
 *
 * `EventSource` can only issue GET requests, but this endpoint is a POST with a JSON body, so we
 * stream the response ourselves via `fetch` + a `ReadableStream` reader and parse SSE frames by
 * hand. The backend drives ElevenLabs → AVTR `/speak` out-of-band (the avatar speaks in its live
 * session); over this connection we only receive:
 *   - `status`  → { phase, correlationId, tMs }   pipeline timeline (llm-first-token, tts-first-audio, avatar-speaking, done…)
 *   - `token`   → { text }                          incremental slice of the on-screen answer
 *   - `error`   → { code, message, recoverable }
 *   - `done`    → { conversationId, correlationId }
 */

export interface StreamStatus {
  phase: string;
  correlationId?: string;
  tMs?: number;
}

export interface StreamError {
  code: string;
  message: string;
  recoverable: boolean;
}

export interface StreamDone {
  conversationId?: string;
  correlationId?: string;
}

export interface AvatarSpeakStreamHandlers {
  onStatus?: (s: StreamStatus) => void;
  onToken?: (t: { text: string }) => void;
  onError?: (e: StreamError) => void;
  onDone?: (d: StreamDone) => void;
}

/**
 * POST `body` to `url` and drive `handlers` from the SSE frames as they arrive. Resolves when the
 * server closes the stream (after its `done` event) and rejects on a transport/HTTP failure or when
 * `signal` aborts (the rejection is a DOMException with name "AbortError").
 */
export async function streamAvatarSpeak(
  url: string,
  body: unknown,
  headers: Record<string, string>,
  handlers: AvatarSpeakStreamHandlers,
  signal?: AbortSignal
): Promise<void> {
  const res = await fetch(url, {
    method: "POST",
    headers: { "Content-Type": "application/json", Accept: "text/event-stream", ...headers },
    body: JSON.stringify(body),
    signal,
  });

  if (!res.ok || !res.body) {
    throw new Error(`avatar-speak-stream failed: ${res.status}`);
  }

  const reader = res.body.getReader();
  const decoder = new TextDecoder();
  let buffer = "";

  // SSE frames are separated by a blank line ("\n\n"). Tolerate CRLF from proxies.
  const dispatchFrame = (frame: string) => {
    let event = "message";
    const dataLines: string[] = [];
    for (const rawLine of frame.split("\n")) {
      const line = rawLine.replace(/\r$/, "");
      if (line.startsWith(":")) continue; // SSE comment / heartbeat
      if (line.startsWith("event:")) {
        event = line.slice(6).trim();
      } else if (line.startsWith("data:")) {
        dataLines.push(line.slice(5).replace(/^ /, ""));
      }
    }
    if (dataLines.length === 0) return;

    let data: any = dataLines.join("\n");
    try {
      data = JSON.parse(data);
    } catch {
      // leave as string; non-JSON frames are ignored below
    }

    switch (event) {
      case "status":
        handlers.onStatus?.(data);
        break;
      case "token":
        handlers.onToken?.(data);
        break;
      case "error":
        handlers.onError?.(data);
        break;
      case "done":
        handlers.onDone?.(data);
        break;
      default:
        break;
    }
  };

  try {
    // eslint-disable-next-line no-constant-condition
    while (true) {
      const { done, value } = await reader.read();
      if (done) break;
      buffer += decoder.decode(value, { stream: true });

      let sep: number;
      while ((sep = indexOfFrameBoundary(buffer)) !== -1) {
        const frame = buffer.slice(0, sep);
        buffer = buffer.slice(sep).replace(/^(\r?\n){2}/, "");
        if (frame.trim()) dispatchFrame(frame);
      }
    }
    // Flush any trailing frame that arrived without a final blank line.
    if (buffer.trim()) dispatchFrame(buffer);
  } finally {
    try {
      reader.releaseLock();
    } catch {
      // ignore
    }
  }
}

/** Index of the start of the "\n\n" (or "\r\n\r\n") frame boundary in `s`, or -1. */
function indexOfFrameBoundary(s: string): number {
  const lf = s.indexOf("\n\n");
  const crlf = s.indexOf("\r\n\r\n");
  if (lf === -1) return crlf;
  if (crlf === -1) return lf;
  return Math.min(lf, crlf);
}
